import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";

import EliminationBracket from "../../../components/EliminationBracket";
import { Icon } from "../../../components/icons";
import { prefersReducedMotion } from "../hooks";
import {
  MOCK_BRACKET_ROUNDS,
  MOCK_CATEGORIES,
  MOCK_CLUB,
  MOCK_ELIM_PLAYERS,
  MOCK_GROUP_A,
  MOCK_LADDER,
  MOCK_THIRD_PLACE,
  MOCK_TOURNAMENT,
} from "../mockData";
import { Avatar, MockFigure, PanelFrame } from "./frames";
import "./FormatsShowcase.css";

/**
 * M5 · Formatos (escena D1 de /organizador). Pestañas accesibles (patrón
 * WAI-ARIA con activación automática y tabindex móvil) con cuatro formatos:
 * el cuadro real de la app (`EliminationBracket`, medidas por omisión) y tres
 * maquetas oscuras. Debajo, la fila de deportes. Todo con datos de ejemplo
 * del universo de `mockData.ts`.
 */

type FormatKey = "eliminacion" | "grupos" | "escalera" | "equipos";

const FORMAT_TABS: ReadonlyArray<{ key: FormatKey; label: string; icon: string }> = [
  { key: "eliminacion", label: "Eliminación directa", icon: "bracket" },
  { key: "grupos", label: "Grupos + liguilla", icon: "grid" },
  { key: "escalera", label: "Escalera", icon: "chart" },
  { key: "equipos", label: "Por equipos", icon: "users" },
];

const [VARONIL, FEMENIL] = MOCK_CATEGORIES;
const P = MOCK_ELIM_PLAYERS;

/* Datos de ejemplo propios de esta maqueta (se derivan del universo de
 * mockData.ts: mismos jugadores, mismo club). */
const DRAW_FACTS = {
  performedAt: "14 oct 2026, 19:00 h (CDMX)",
  method: "Sorteo estándar internacional (ITF/USTA)",
  seeds: "2 sembrados en cuadro de 8",
};

type Side = "home" | "away";

interface TieLine {
  label: string;
  home: string;
  away: string;
  winner: Side;
  /** Marcador desde el lado del ganador, como se anota en tenis. */
  sets: string[];
}

function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] ?? name;
}

const TEAM_TIE: { home: string; away: string; score: [number, number]; lines: TieLine[] } = {
  home: "Equipo Norte",
  away: "Equipo Sur",
  score: [2, 1],
  lines: [
    { label: "Single 1", home: P.navarro, away: P.castro, winner: "home", sets: ["6-4", "6-2"] },
    { label: "Single 2", home: P.pena, away: P.aguilar, winner: "away", sets: ["7-5", "6-4"] },
    {
      label: "Dobles",
      home: `${surname(P.navarro)} / ${surname(P.pena)}`,
      away: `${surname(P.castro)} / ${surname(P.aguilar)}`,
      winner: "home",
      sets: ["6-3", "3-6", "[10-7]"],
    },
  ],
};

const SPORTS: ReadonlyArray<{ name: string; line: string; sets: string[]; glyph: ReactNode }> = [
  { name: "Tenis", line: "Sets con muerte súbita y súper muerte súbita", sets: ["6-4", "3-6", "[10-8]"], glyph: <Icon name="tennis" /> },
  { name: "Pádel", line: "Siempre en parejas", sets: ["6-4", "7-5"], glyph: <PadelGlyph /> },
  { name: "Pickleball", line: "Juegos a 11, al mejor de 3", sets: ["11-8", "9-11", "11-7"], glyph: <PickleballGlyph /> },
];

function parseRecord(record: string): { played: number; won: number; lost: number } {
  const [won = 0, lost = 0] = record.split("-").map((part) => Number.parseInt(part, 10) || 0);
  return { played: won + lost, won, lost };
}

/** Índice de destino para una tecla dentro de la lista de pestañas. */
function targetTabIndex(key: string, current: number, count: number): number | null {
  switch (key) {
    case "ArrowRight":
      return (current + 1) % count;
    case "ArrowLeft":
      return (current - 1 + count) % count;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return null;
  }
}

/** Lleva la pestaña a la vista dentro de la fila desplazable (celular) sin
 * mover la página en vertical. */
function revealTab(list: HTMLElement | null, tab: HTMLElement | null): void {
  if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
  const gutter = 12;
  const left = tab.offsetLeft - gutter;
  const right = tab.offsetLeft + tab.offsetWidth + gutter;
  let target: number;
  if (left < list.scrollLeft) target = left;
  else if (right > list.scrollLeft + list.clientWidth) target = right - list.clientWidth;
  else return;
  if (typeof list.scrollTo === "function") {
    list.scrollTo({ left: Math.max(0, target), behavior: prefersReducedMotion() ? "auto" : "smooth" });
  } else {
    list.scrollLeft = Math.max(0, target);
  }
}

/** Con el panel enfocado, ← y → desplazan el cuadro que no cabe a lo ancho:
 * la maqueta es inerte (sin elementos enfocables propios), así que sin esto un
 * usuario de teclado no podría ver la final ni el 3er lugar. */
function scrollBracketWithKeys(event: KeyboardEvent<HTMLDivElement>): void {
  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const scroller = event.currentTarget.querySelector<HTMLElement>(".mk-formats__scroller");
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 1) return;
  event.preventDefault();
  const step = Math.max(120, Math.round(scroller.clientWidth * 0.6));
  const left = scroller.scrollLeft + (event.key === "ArrowRight" ? step : -step);
  if (typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ left, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  } else {
    scroller.scrollLeft = left;
  }
}

/** true si el contenido se sale a lo ancho; null si no se puede medir (la
 * indicación de desplazamiento cae entonces a la regla de CSS ≤1180 px). */
function useHorizontalOverflow(ref: RefObject<HTMLElement>): boolean | null {
  const [overflow, setOverflow] = useState<boolean | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const measure = () => setOverflow(node.scrollWidth > node.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return overflow;
}

export function FormatsShowcase() {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number, moveFocus: boolean) => {
    setActive(index);
    const tab = tabRefs.current[index] ?? null;
    if (moveFocus) tab?.focus({ preventScroll: true });
    revealTab(listRef.current, tab);
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = targetTabIndex(event.key, active, FORMAT_TABS.length);
    if (next === null) return;
    event.preventDefault();
    select(next, true);
  };

  return (
    <div className="t123-formats">
      <div ref={listRef} className="t123-formats__tabs" role="tablist" aria-label="Formatos de torneo">
        {FORMAT_TABS.map((tab, index) => {
          const selected = index === active;
          return (
            <button
              key={tab.key}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.key}`}
              aria-controls={`${baseId}-panel-${tab.key}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={`t123-formats__tab${selected ? " is-active" : ""}`}
              onClick={() => select(index, false)}
              onKeyDown={onTabKeyDown}
            >
              <Icon name={tab.icon} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="t123-formats__stage t123-bracket-card">
        {FORMAT_TABS.map((tab, index) => (
          <div
            key={tab.key}
            role="tabpanel"
            id={`${baseId}-panel-${tab.key}`}
            aria-labelledby={`${baseId}-tab-${tab.key}`}
            tabIndex={0}
            hidden={index !== active}
            onKeyDown={tab.key === "eliminacion" ? scrollBracketWithKeys : undefined}
            className={`t123-formats__panel t123-formats__panel--${tab.key}${tab.key === "eliminacion" ? "" : " t123-formats__panel--canvas"}`}
          >
            <FormatPanel format={tab.key} />
          </div>
        ))}
      </div>

      <ul className="t123-formats__sports" aria-label="Deportes">
        {SPORTS.map((sport) => (
          <li key={sport.name} className="t123-formats__sport">
            <span className="t123-tile-icon" aria-hidden="true">
              {sport.glyph}
            </span>
            <div>
              <strong>{sport.name}</strong>
              <p>{sport.line}</p>
              <span className="t123-formats__score">
                <span className="t123-sr-only">Marcador de ejemplo: </span>
                {sport.sets.map((set, i) => (
                  <span key={i}>{set}</span>
                ))}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <p className="t123-formats__note">El deporte se elige al crear cada torneo.</p>
    </div>
  );
}

function FormatPanel({ format }: { format: FormatKey }) {
  switch (format) {
    case "eliminacion":
      return <EliminationPanel />;
    case "grupos":
      return <GroupsPanel />;
    case "escalera":
      return <LadderPanel />;
    case "equipos":
      return <TeamsPanel />;
  }
}

function EliminationPanel() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const overflow = useHorizontalOverflow(scrollerRef);
  const hintState = overflow === null ? " is-auto" : overflow ? " is-on" : "";
  return (
    <MockFigure
      className="t123-formats__fig t123-formats__fig--bracket"
      label={`Cuadro de eliminación directa de ejemplo de ${VARONIL}, cuadro de 8 con 2 sembrados: ${P.salazar}, sembrado 1, gana la final a ${P.ruiz}, sembrado 2, y ${P.herrera} gana el partido por el 3er lugar.`}
    >
      <div className="mk-formats__band">
        <div className="mk-formats__band-title">
          <strong>{VARONIL}</strong>
          <span>
            {MOCK_TOURNAMENT.name} · Cuadro de 8
          </span>
        </div>
        <ul className="mk-formats__facts">
          <li>
            <Icon name="check" strokeWidth={3} />
            <span>
              <b>Sorteo realizado</b> el {DRAW_FACTS.performedAt}
            </span>
          </li>
          <li>{DRAW_FACTS.method}</li>
          <li>{DRAW_FACTS.seeds}</li>
        </ul>
      </div>
      <p className={`mk-formats__hint${hintState}`}>
        <span className="mk-formats__hint-touch">Desliza para ver todo el cuadro</span>
        <span className="mk-formats__hint-keys">Usa las flechas ← → para ver todo el cuadro</span> <i>→</i>
      </p>
      {/* tabIndex -1: la maqueta es una imagen para lectores de pantalla
          (aria-hidden); Chrome volvería tabulable un contenedor desplazable.
          Con teclado se desplaza desde el panel (scrollBracketWithKeys). */}
      <div ref={scrollerRef} className="mk-formats__scroller t123-scroll-x" tabIndex={-1}>
        <div className="mk-formats__bracket">
          <EliminationBracket rounds={MOCK_BRACKET_ROUNDS} thirdPlace={MOCK_THIRD_PLACE} />
        </div>
      </div>
    </MockFigure>
  );
}

function GroupsPanel() {
  return (
    <MockFigure
      className="t123-formats__fig t123-formats__fig--dark"
      label={`Tabla de ejemplo del Grupo A de ${FEMENIL}: ${MOCK_GROUP_A.map((row, i) => `${i + 1}. ${row.name}, ${row.record}`).join("; ")}. Las dos primeras pasan a la liguilla.`}
    >
      <PanelFrame title={`Grupo A · ${FEMENIL}`} className="mk-formats__panel">
        <div className="mk-formats__table mk-formats__table--group">
          <div className="mk-formats__thead">
            <span>#</span>
            <span>Jugadora</span>
            <span>PJ</span>
            <span>G</span>
            <span>P</span>
          </div>
          {MOCK_GROUP_A.map((row, i) => {
            const { played, won, lost } = parseRecord(row.record);
            const through = i < 2;
            return (
              <div key={row.name} className={`mk-formats__row${through ? " is-through" : ""}`}>
                <span className="mk-formats__pos">{i + 1}</span>
                <span className="mk-formats__name">
                  <Avatar name={row.name} />
                  <span>{row.name}</span>
                </span>
                <span className="mk-formats__num">{played}</span>
                <span className="mk-formats__num mk-formats__num--strong">{won}</span>
                <span className="mk-formats__num">{lost}</span>
              </div>
            );
          })}
        </div>
        <p className="mk-formats__through">
          <i aria-hidden="true" />
          Pasan a la liguilla: 1º y 2º de cada grupo
        </p>
      </PanelFrame>
    </MockFigure>
  );
}

function Movement({ move }: { move: number }) {
  if (move === 0) return <span className="mk-formats__move mk-formats__move--same">—</span>;
  const up = move > 0;
  return (
    <span className={`mk-formats__move ${up ? "mk-formats__move--up" : "mk-formats__move--down"}`}>
      <svg viewBox="0 0 10 10" width="0.7em" height="0.7em" aria-hidden="true">
        <path d={up ? "M5 1.5 9 8.5H1Z" : "M5 8.5 1 1.5h8Z"} fill="currentColor" />
      </svg>
      {Math.abs(move)}
    </span>
  );
}

function LadderPanel() {
  return (
    <MockFigure
      className="t123-formats__fig t123-formats__fig--dark"
      label={`Escalera de ejemplo de la Liga Arboleda con la jornada 7 publicada: ${MOCK_LADDER.map((row, i) => `${i + 1}. ${row.name}`).join("; ")}, con los lugares que subió o bajó cada jugadora.`}
    >
      <PanelFrame title="Liga Arboleda" badge="Jornada 7 publicada" className="mk-formats__panel">
        <div className="mk-formats__table mk-formats__table--ladder">
          <div className="mk-formats__thead">
            <span>#</span>
            <span>Jugadora</span>
            <span>Movimiento</span>
          </div>
          {MOCK_LADDER.map((row, i) => (
            <div key={row.name} className={`mk-formats__row${i === 0 ? " is-leader" : ""}`}>
              <span className="mk-formats__pos">{i + 1}</span>
              <span className="mk-formats__name">
                <Avatar name={row.name} />
                <span>{row.name}</span>
              </span>
              <Movement move={row.move} />
            </div>
          ))}
        </div>
      </PanelFrame>
    </MockFigure>
  );
}

function TieSide({ name, side, won }: { name: string; side: Side; won: boolean }) {
  return (
    <span className={`mk-formats__side mk-formats__side--${side}${won ? " is-win" : ""}`}>
      {won && side === "away" ? <Icon name="check" strokeWidth={3} className="mk-formats__win" /> : null}
      <span className="mk-formats__side-name">{name}</span>
      {won && side === "home" ? <Icon name="check" strokeWidth={3} className="mk-formats__win" /> : null}
    </span>
  );
}

function TeamsPanel() {
  const [homePoints, awayPoints] = TEAM_TIE.score;
  return (
    <MockFigure
      className="t123-formats__fig t123-formats__fig--dark t123-formats__fig--teams"
      label={`Serie por equipos de ejemplo: ${TEAM_TIE.home} gana ${homePoints} a ${awayPoints} a ${TEAM_TIE.away} con dos singles y un dobles.`}
    >
      <PanelFrame title={`${MOCK_CLUB.name} · Serie por equipos`} className="mk-formats__panel">
        <div className="mk-formats__tie-head">
          <span className="mk-formats__team">
            <Avatar name={TEAM_TIE.home} className="mk-formats__team-avatar is-win" />
            <strong>{TEAM_TIE.home}</strong>
          </span>
          <span className="mk-formats__tie-score">
            <b className={homePoints > awayPoints ? "is-win" : ""}>{homePoints}</b>
            <span>–</span>
            <b className={awayPoints > homePoints ? "is-win" : ""}>{awayPoints}</b>
          </span>
          <span className="mk-formats__team mk-formats__team--away">
            <strong>{TEAM_TIE.away}</strong>
            <Avatar name={TEAM_TIE.away} className="mk-formats__team-avatar" />
          </span>
        </div>
        <p className="mk-formats__tie-rule">2 singles + 1 dobles · gana con 2</p>
        <ol className="mk-formats__lines">
          {TEAM_TIE.lines.map((line) => (
            <li key={line.label} className="mk-formats__line">
              <span className="mk-formats__line-label">{line.label}</span>
              <TieSide name={line.home} side="home" won={line.winner === "home"} />
              <span className="mk-formats__sets">
                {line.sets.map((set, i) => (
                  <span key={i}>{set}</span>
                ))}
              </span>
              <TieSide name={line.away} side="away" won={line.winner === "away"} />
            </li>
          ))}
        </ol>
      </PanelFrame>
    </MockFigure>
  );
}

/* Íconos de deporte con el mismo trazo que components/icons.tsx. */
function PadelGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M12 2.5c3.8 0 6.5 2.7 6.5 6.5 0 3.4-2.2 6.1-4.9 7.1h-3.2C7.7 15.1 5.5 12.4 5.5 9c0-3.8 2.7-6.5 6.5-6.5Z" />
      <path d="M10.6 16.1 10.2 21.5h3.6l-.4-5.4" />
      <g fill="currentColor" stroke="none">
        <circle cx="9.6" cy="7.4" r="0.95" />
        <circle cx="14.4" cy="7.4" r="0.95" />
        <circle cx="12" cy="9.9" r="0.95" />
        <circle cx="9.6" cy="12.3" r="0.95" />
        <circle cx="14.4" cy="12.3" r="0.95" />
      </g>
    </svg>
  );
}

function PickleballGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="2.5" y="2.5" width="11.5" height="12" rx="4.2" />
      <path d="M7 14.5v5.6a1.25 1.25 0 0 0 2.5 0v-5.6" />
      <circle cx="18.3" cy="17.6" r="3.4" />
      <g fill="currentColor" stroke="none">
        <circle cx="17.2" cy="16.5" r="0.7" />
        <circle cx="19.5" cy="17.2" r="0.7" />
        <circle cx="18" cy="19.1" r="0.7" />
      </g>
    </svg>
  );
}
